import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, X, Save, Search, Upload, Tag, Camera, Image as ImageIcon } from "lucide-react";
import { compressImage } from "./StockForm";
import { Lightbox } from "./Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");

const CATS = ["Laptop", "Desktop", "Monitor", "Display", "Storage", "Printer", "UPS", "Tablet", "Networking", "Office", "Furniture", "Other"];
const IMGDEFAULT = "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=900&q=80";
const empty = { code: "", type: "", category: "Laptop", name: "", brand: "", qty: 1, price: 0, img: IMGDEFAULT, featured: false, active: true, sold: false, price_on_request: false };

export default function AdminInventory({ adminKey }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // product or null
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lb, setLb] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const onEditPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    try {
      const photo = await compressImage(f);
      setEditing((ed) => ({
        ...ed,
        img: photo,
        images: [photo, ...(Array.isArray(ed.images) ? ed.images.slice(1) : [])],
      }));
    } catch (_) {
      alert("Could not read that photo. Please try another one.");
    }
    e.target.value = "";
  };

  const headers = { "Content-Type": "application/json", "X-Admin-Key": adminKey };

  const load = useCallback(() => {
    fetch(`${API}/products?include_inactive=true`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...editing, qty: Number(editing.qty) || 0, price: Number(editing.price) || 0 };
    const isNew = !editing.id;
    const url = isNew ? `${API}/products` : `${API}/products/${editing.id}`;
    try {
      const r = await fetch(url, { method: isNew ? "POST" : "PUT", headers, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      setEditing(null);
      load();
    } catch (_) { alert("Save failed"); }
    finally { setSaving(false); }
  };

  const patch = async (p, changes) => {
    await fetch(`${API}/products/${p.id}`, {
      method: "PUT", headers,
      body: JSON.stringify({ ...p, ...changes }),
    });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await fetch(`${API}/products/${p.id}`, { method: "DELETE", headers });
    load();
  };

  const onUpload = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!window.confirm(`Replace ALL current stock with the items in "${f.name}"? This cannot be undone.`)) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("mode", "replace");
    try {
      const r = await fetch(`${API}/products/bulk_upload`, {
        method: "POST", headers: { "X-Admin-Key": adminKey }, body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      alert(`Imported ${data.imported} items. Total stock is now ${data.total}.`);
      load();
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const term = q.trim().toLowerCase();
  const list = items.filter((p) => !term || `${p.code || ""} ${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(term));
  const lowCount = items.filter((p) => !p.sold && (Number(p.qty) || 0) <= 1).length;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex max-w-full flex-wrap items-center gap-3">
          <div className="flex min-w-0 items-center gap-2 rounded-full border bg-white px-4 py-2" style={{ borderColor: "var(--line)" }}>
            <Search size={16} style={{ color: "var(--muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, name, brand…"
                   data-testid="inv-search"
                   className="w-36 bg-transparent text-[14px] outline-none sm:w-56" />
          </div>
          {lowCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 font-heading text-[12.5px] font-bold"
                  style={{ background: "rgba(244,119,28,0.14)", color: "var(--orange-700)" }}>
              <Tag size={13} /> {lowCount} low stock
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onUpload} className="hidden" />
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
                  className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-2.5 font-heading text-[13.5px] font-bold transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ borderColor: "var(--green-800)", color: "var(--green-900)" }}>
            <Upload size={16} /> {uploading ? "Uploading\u2026" : "Upload Excel"}
          </button>
          <button onClick={() => setEditing({ ...empty })}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-heading text-[13.5px] font-bold text-white"
                  style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
            <Plus size={16} /> Add product
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border bg-white" style={{ borderColor: "var(--line)" }}>
        <table className="w-full min-w-[900px] text-left text-[13.5px]">
          <thead>
            <tr style={{ background: "var(--green-100)", color: "var(--green-950)" }} className="font-heading text-[12px] uppercase tracking-wide">
              <th className="px-3 py-3">Item</th><th className="px-3 py-3">Code</th><th className="px-3 py-3">Category</th><th className="px-3 py-3">Brand</th>
              <th className="px-3 py-3">Qty</th><th className="px-3 py-3">Price</th><th className="px-3 py-3">Featured</th>
              <th className="px-3 py-3">Active</th><th className="px-3 py-3">Sold</th><th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t align-middle" style={{ borderColor: "var(--line)", color: "#31463a", opacity: p.active ? 1 : 0.5 }}>
                <td className="px-3 py-2.5 max-w-[320px]">
                  <div className="flex items-center gap-2.5">
                    <img src={p.img} alt="" onClick={() => setLb({ images: (p.images && p.images.length ? p.images : [p.img]).filter(Boolean), alt: p.name, url: `${window.location.origin}/product/${p.id}` })}
                         data-testid={`inv-photo-zoom-${p.id}`}
                         className="h-9 w-9 flex-shrink-0 cursor-zoom-in rounded-md object-cover" />
                    <span className="font-semibold" style={{ color: "var(--green-950)" }}>{p.name}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 font-heading text-[12.5px] font-extrabold" style={{ color: "var(--orange-700)" }} data-testid={`inv-code-${p.id}`}>{p.code || "—"}</td>
                <td className="px-3 py-2.5">{p.category}</td>
                <td className="px-3 py-2.5">{p.brand}</td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    {p.qty}
                    {!p.sold && p.qty <= 1 && (
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-extrabold text-white" style={{ background: "linear-gradient(135deg, #ff3f1f, var(--orange-500))" }}>LOW</span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-semibold" style={{ color: "var(--orange-700)" }}>{p.price_on_request ? <span style={{ color: "var(--green-800)" }}>On request</span> : inr(p.price)}</td>
                <td className="px-3 py-2.5">
                  <button onClick={() => patch(p, { featured: !p.featured })} aria-label="Toggle featured"
                          className="grid h-8 w-8 place-items-center rounded-lg"
                          style={{ background: p.featured ? "var(--yellow-500)" : "var(--green-100)", color: p.featured ? "var(--green-950)" : "var(--muted)" }}>
                    <Star size={15} fill={p.featured ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="px-3 py-2.5">
                  <button onClick={() => patch(p, { active: !p.active })} aria-label="Toggle active"
                          className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: "var(--green-100)", color: "var(--green-800)" }}>
                    {p.active ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                </td>
                <td className="px-3 py-2.5">
                  <button onClick={() => patch(p, { sold: !p.sold })} aria-label="Toggle sold"
                          className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 font-heading text-[12px] font-bold"
                          style={p.sold ? { background: "var(--orange-700)", color: "#fff" } : { background: "var(--green-100)", color: "var(--muted)" }}>
                    <Tag size={13} /> {p.sold ? "Sold" : "Mark"}
                  </button>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => setEditing(p)} className="grid h-8 w-8 place-items-center rounded-lg border" style={{ borderColor: "var(--line)", color: "var(--green-800)" }} aria-label="Edit"><Pencil size={14} /></button>
                    <button onClick={() => remove(p)} className="grid h-8 w-8 place-items-center rounded-lg border" style={{ borderColor: "var(--line)", color: "var(--orange-700)" }} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(31,74,56,0.45)" }} onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save}
                className="w-full max-w-lg rounded-2xl bg-white p-6" style={{ boxShadow: "var(--shadow)", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-[20px] font-extrabold" style={{ color: "var(--green-950)" }}>{editing.id ? "Edit product" : "Add product"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="grid h-9 w-9 place-items-center rounded-full" style={{ background: "var(--green-100)" }}><X size={18} /></button>
            </div>
            <div className="grid gap-3">
              <Field label="Name"><input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="inp" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Item Code"><input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })} placeholder="LAPDL100" className="inp" /></Field>
                <Field label="Brand"><input value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="inp" data-testid="inv-category-select">
                    {Array.from(new Set([...items.map((i) => i.category).filter(Boolean), ...CATS, editing.category].filter(Boolean))).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Qty"><input type="number" min={0} value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Price (₹)"><input type="number" min={0} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="inp" /></Field>
              </div>
              <div>
                <span className="font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>Product Photo</span>
                <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onEditPhoto}
                       className="hidden" data-testid="inv-photo-camera-input" />
                <input ref={galRef} type="file" accept="image/*" onChange={onEditPhoto}
                       className="hidden" data-testid="inv-photo-gallery-input" />
                <div className="mt-2 flex items-center gap-3">
                  {editing.img && (
                    <img src={editing.img} alt="Product" data-testid="inv-photo-preview"
                         onClick={() => setLb({ images: (editing.images && editing.images.length ? editing.images : [editing.img]).filter(Boolean), alt: editing.name })}
                         className="h-20 w-20 shrink-0 cursor-zoom-in rounded-lg border object-cover" style={{ borderColor: "#d8c8af" }} />
                  )}
                  <div className="grid w-full grid-cols-2 gap-2">
                    <button type="button" onClick={() => camRef.current?.click()} data-testid="inv-take-photo"
                            className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 font-heading text-[12.5px] font-bold"
                            style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.55)" }}>
                      <Camera size={15} style={{ color: "#E8501A" }} /> Take Photo
                    </button>
                    <button type="button" onClick={() => galRef.current?.click()} data-testid="inv-choose-gallery"
                            className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 font-heading text-[12.5px] font-bold"
                            style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.55)" }}>
                      <ImageIcon size={15} style={{ color: "#E8501A" }} /> Choose from Gallery
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-5 pt-1">
                <label className="flex items-center gap-2 font-heading text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured (Hot Stock)
                </label>
                <label className="flex items-center gap-2 font-heading text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>
                  <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Visible on site
                </label>
                <label className="flex items-center gap-2 font-heading text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>
                  <input type="checkbox" checked={!!editing.sold} onChange={(e) => setEditing({ ...editing, sold: e.target.checked })} /> Sold
                </label>
                <label className="flex items-center gap-2 font-heading text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>
                  <input type="checkbox" checked={!!editing.price_on_request} onChange={(e) => setEditing({ ...editing, price_on_request: e.target.checked })} /> Price on request
                </label>
              </div>
            </div>
            <button type="submit" disabled={saving}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 font-heading font-bold text-white disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
              <Save size={17} /> {saving ? "Saving\u2026" : "Save product"}
            </button>
          </form>
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid #d8c8af;border-radius:8px;padding:10px 12px;font-size:14px;outline:none;background:#fff}.inp:focus{box-shadow:0 0 0 3px rgba(245,180,0,0.3)}`}</style>
      {lb && <Lightbox images={lb.images} alt={lb.alt} shareUrl={lb.url} onClose={() => setLb(null)} />}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="grid gap-1.5 font-heading text-[13px] font-semibold" style={{ color: "var(--green-950)" }}>
      {label}
      {children}
    </label>
  );
}
